/**
 * Grand Quack Auto — duck geometry builders.
 *
 * Pure functions returning named THREE.Group / Mesh for each body part.
 * All dimensions from config.ts. All materials MeshToonMaterial.
 * Assembled into the full duck hierarchy by duck.ts createDuck().
 *
 * Target look: cute rubber duck / cartoon duckling matching marketing materials.
 */

import * as THREE from 'three';
import { DUCK, PALETTE } from '../config';

const mat = (color: number) => new THREE.MeshToonMaterial({ color });

/** Egg-shaped body — scaled sphere, wider at bottom. */
export function buildBody(): THREE.Mesh {
  const geom = new THREE.SphereGeometry(DUCK.bodyRadius, 16, 12);
  const mesh = new THREE.Mesh(geom, mat(PALETTE.duck));
  mesh.name = 'body';
  mesh.scale.set(1.0, 0.85, 0.9);
  mesh.position.y = DUCK.bodyRadius * 0.85;
  mesh.castShadow = true;
  return mesh;
}

/** Head group: sphere + flat bill beak + large eyes + pupils. */
export function buildHead(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'head';
  const r = DUCK.headRadius;

  // Head sphere — sits on top of body, slightly forward.
  const sphere = new THREE.Mesh(
    new THREE.SphereGeometry(r, 14, 10),
    mat(PALETTE.duck),
  );
  sphere.castShadow = true;
  group.add(sphere);

  // Beak — flat duck bill using flattened cylinder (6 segments = hexagonal).
  const beak = new THREE.Mesh(
    new THREE.CylinderGeometry(DUCK.beakWidth * 0.4, DUCK.beakWidth * 0.6, DUCK.beakLength, 6),
    mat(PALETTE.duckBeak),
  );
  beak.name = 'beak';
  beak.rotation.x = Math.PI / 2;
  beak.scale.y = 0.4; // flatten vertically for duck-bill shape
  beak.position.set(0, -r * 0.1, r + DUCK.beakLength * 0.35);
  beak.castShadow = true;
  group.add(beak);

  // Eyes — large, expressive.
  const eyeR = r * 0.28;
  const eyeGeom = new THREE.SphereGeometry(eyeR, 8, 6);
  const eyeMat = mat(PALETTE.duckEye);
  const eyeY = r * 0.25;
  const eyeZ = r * 0.7;
  const eyeSpread = r * 0.6;

  const leftEye = new THREE.Mesh(eyeGeom, eyeMat);
  leftEye.position.set(-eyeSpread, eyeY, eyeZ);
  const rightEye = new THREE.Mesh(eyeGeom, eyeMat);
  rightEye.position.set(eyeSpread, eyeY, eyeZ);
  group.add(leftEye, rightEye);

  // Pupils — dark, ~50% of eye radius.
  const pupilR = eyeR * 0.5;
  const pupilGeom = new THREE.SphereGeometry(pupilR, 6, 4);
  const pupilMat = new THREE.MeshBasicMaterial({ color: PALETTE.duckPupil });
  const lp = new THREE.Mesh(pupilGeom, pupilMat);
  lp.position.copy(leftEye.position);
  lp.position.z += eyeR * 0.55;
  const rp = new THREE.Mesh(pupilGeom, pupilMat);
  rp.position.copy(rightEye.position);
  rp.position.z += eyeR * 0.55;
  group.add(lp, rp);

  // Position head above body.
  group.position.set(0, DUCK.bodyRadius * 1.5, DUCK.bodyRadius * 0.15);
  return group;
}

/** Left + right wing groups. Mesh offset so rotation pivots from shoulder. */
export function buildWings(): [THREE.Group, THREE.Group] {
  const wingGeom = new THREE.SphereGeometry(DUCK.wingSize, 8, 6);
  const wingMat = mat(PALETTE.duck);

  function makeWing(side: 1 | -1, name: string): THREE.Group {
    const group = new THREE.Group();
    group.name = name;
    const mesh = new THREE.Mesh(wingGeom, wingMat);
    mesh.scale.set(0.4, 0.8, 1.0); // flattened, vertically elongated
    mesh.position.y = -DUCK.wingSize * 0.4; // pivot from shoulder (top of wing)
    mesh.castShadow = true;
    group.add(mesh);
    group.position.set(
      side * DUCK.bodyRadius * 0.85,
      DUCK.bodyRadius * 0.9,
      0,
    );
    return group;
  }

  return [makeWing(-1, 'leftWing'), makeWing(1, 'rightWing')];
}

/** Tail — small cone pointing up+back. Key directional cue. */
export function buildTail(): THREE.Mesh {
  const geom = new THREE.ConeGeometry(0.08, DUCK.tailHeight, 4);
  const mesh = new THREE.Mesh(geom, mat(PALETTE.duck));
  mesh.name = 'tail';
  mesh.rotation.x = -0.5; // tilt backward ~30 degrees
  mesh.position.set(0, DUCK.bodyRadius * 1.2, -DUCK.bodyRadius * 0.7);
  mesh.castShadow = true;
  return mesh;
}

/** Left + right webbed feet. Flat triangle (3-segment cylinder). */
export function buildFeet(): [THREE.Group, THREE.Group] {
  const footGeom = new THREE.CylinderGeometry(
    DUCK.footRadius * 0.6, DUCK.footRadius, 0.03, 3,
  );
  const footMat = mat(PALETTE.duckBeak); // orange like beak

  function makeFoot(side: 1 | -1, name: string): THREE.Group {
    const group = new THREE.Group();
    group.name = name;
    const mesh = new THREE.Mesh(footGeom, footMat);
    mesh.rotation.y = Math.PI / 6; // rotate so one point faces forward
    mesh.castShadow = true;
    group.add(mesh);
    group.position.set(side * 0.12, 0.015, 0.05);
    return group;
  }

  return [makeFoot(-1, 'leftFoot'), makeFoot(1, 'rightFoot')];
}
